import {LocaleDateTimeFormat} from 'platform/locale';

import {isNil, isNotNil} from 'ramda';

import {buildArray, EMPTY_PLACEHOLDER, Nullish, parseDate} from 'shared/web';

import {DocumentationAttribute} from '../components/DocumentationItem/DocumentationItem';
import {DcLocale} from '../hooks/useLocale';
import {getServiceHistory} from './getServiceHistory';
import {getValueWithUnit} from './getValueWithUnit';

const isAttributeVisible = (
  attribute: {label: string | Nullish; value: string | number | Nullish},
  showEmptyFields: boolean
) => {
  if (isNil(attribute?.label)) {
    return false;
  }
  if (!showEmptyFields && isNil(attribute?.value)) {
    return false;
  }

  return true;
};

export const getServiceHistoryFeatures = (
  serviceHistory: ReturnType<typeof getServiceHistory>,
  formatDateTime: (dateTimeFormat: LocaleDateTimeFormat, value: Date) => string,
  locale: DcLocale,
  showEmptyFields: boolean
) => {
  if (!serviceHistory) {
    return null;
  }
  const getDate = (value: string | number | Nullish) =>
    isNotNil(value) ? formatDateTime('dateShort', parseDate(value)) : null;

  if (!serviceHistory) {
    return null;
  }

  const serviceHistoryLabeledValues = {
    regularServiceIntervalPerformed: {
      label: serviceHistory?.inspections?.regularServiceIntervalPerformed?.label,
      value: serviceHistory?.inspections?.regularServiceIntervalPerformed?.value,
    },
    dateOfLastService: {
      label: serviceHistory?.inspections?.dateOfLastService?.label,
      value: getDate(serviceHistory?.inspections?.dateOfLastService?.value),
    },
    dateOfNextService: {
      label: serviceHistory?.inspections?.dateOfNextService?.label,
      value: getDate(serviceHistory?.inspections?.dateOfNextService?.value),
    },
    mileageOfLastService: {
      label: serviceHistory?.inspections?.mileageOfLastService?.label,
      value: getValueWithUnit({
        localeConfig: locale.localeConfig,
        auditValue: serviceHistory?.inspections?.mileageOfLastService?.value,
      }),
    },
    mileageOfNextService: {
      label: serviceHistory?.inspections?.mileageOfNextService?.label,
      value: getValueWithUnit({
        localeConfig: locale.localeConfig,
        auditValue: serviceHistory?.inspections?.mileageOfNextService?.value,
      }),
    },
  };

  const lastOilChangeLabeledValues = {
    dateOfLastOilChange: {
      label: serviceHistory?.lastOilChange?.dateOfLastOilChange?.label ?? '',
      value: getDate(serviceHistory?.lastOilChange?.dateOfLastOilChange?.value),
    },
    amountOfKmDrivenFromOilChange: {
      label: serviceHistory?.lastOilChange?.amountOfKmDrivenFromOilChange?.label ?? '',
      value: getValueWithUnit({
        localeConfig: locale.localeConfig,
        auditValue: serviceHistory?.lastOilChange?.amountOfKmDrivenFromOilChange?.value,
      }),
    },
  };

  const lastBrakeFluidChangeLabeledValues = {
    dateOfLastBrakeFluidChange: {
      label: serviceHistory?.lastBrakeFluidChange?.dateOfLastBrakeFluidChange?.label,
      value: getDate(serviceHistory?.lastBrakeFluidChange?.dateOfLastBrakeFluidChange?.value),
    },
    amountOfKmDrivenFromBrakeFluidChange: {
      label: serviceHistory?.lastBrakeFluidChange?.amountOfKmDrivenFromBrakeFluidChange?.label,
      value: getValueWithUnit({
        localeConfig: locale.localeConfig,
        auditValue:
          serviceHistory?.lastBrakeFluidChange?.amountOfKmDrivenFromBrakeFluidChange?.value,
      }),
    },
  };

  const lastOilChangeInGearboxLabeledValues = {
    dateOfLastOilChangeInGearbox: {
      label: serviceHistory?.lastOilChangeInGearbox?.dateOfLastOilChangeInGearbox?.label,
      value: getDate(serviceHistory?.lastOilChangeInGearbox?.dateOfLastOilChangeInGearbox?.value),
    },
    amountOfKmDrivenFromOilChangeInGearbox: {
      label: serviceHistory?.lastOilChangeInGearbox?.amountOfKmDrivenFromOilChangeInGearbox?.label,
      value: getValueWithUnit({
        localeConfig: locale.localeConfig,
        auditValue:
          serviceHistory?.lastOilChangeInGearbox?.amountOfKmDrivenFromOilChangeInGearbox?.value,
      }),
    },
  };

  const lastOilChangeInDifferentialLabeledValues = {
    dateOfLastOilChangeInDifferential: {
      label: serviceHistory?.lastOilChangeInDifferential?.dateOfLastOilChangeInDifferential?.label,
      value: getDate(
        serviceHistory?.lastOilChangeInDifferential?.dateOfLastOilChangeInDifferential?.value
      ),
    },
    amountOfKmDrivenFromOilChangeInDifferential: {
      label:
        serviceHistory?.lastOilChangeInDifferential?.amountOfKmDrivenFromOilChangeInDifferential
          ?.label,
      value: getValueWithUnit({
        localeConfig: locale.localeConfig,
        auditValue:
          serviceHistory?.lastOilChangeInDifferential?.amountOfKmDrivenFromOilChangeInDifferential
            ?.value,
      }),
    },
  };

  const serviceHistoryAttributes = buildArray<DocumentationAttribute>()
    .when(
      isAttributeVisible(
        serviceHistoryLabeledValues.regularServiceIntervalPerformed,
        showEmptyFields
      ),
      {
        label: serviceHistoryLabeledValues.regularServiceIntervalPerformed.label ?? '',
        value:
          serviceHistoryLabeledValues.regularServiceIntervalPerformed.value ?? EMPTY_PLACEHOLDER,
      }
    )
    .when(isAttributeVisible(serviceHistoryLabeledValues.dateOfLastService, showEmptyFields), {
      label: serviceHistoryLabeledValues.dateOfLastService.label ?? '',
      value: serviceHistoryLabeledValues.dateOfLastService.value ?? EMPTY_PLACEHOLDER,
    })
    .when(isAttributeVisible(serviceHistoryLabeledValues.dateOfNextService, showEmptyFields), {
      label: serviceHistoryLabeledValues.dateOfNextService.label ?? '',
      value: serviceHistoryLabeledValues.dateOfNextService.value ?? EMPTY_PLACEHOLDER,
    })
    .when(isAttributeVisible(serviceHistoryLabeledValues.mileageOfLastService, showEmptyFields), {
      label: serviceHistoryLabeledValues.mileageOfLastService.label ?? '',
      value: serviceHistoryLabeledValues.mileageOfLastService.value ?? EMPTY_PLACEHOLDER,
    })
    .when(isAttributeVisible(serviceHistoryLabeledValues.mileageOfNextService, showEmptyFields), {
      label: serviceHistoryLabeledValues.mileageOfNextService.label ?? '',
      value: serviceHistoryLabeledValues.mileageOfNextService.value ?? EMPTY_PLACEHOLDER,
    });

  const lastOilChangeAttributes = buildArray<DocumentationAttribute>()
    .when(isAttributeVisible(lastOilChangeLabeledValues.dateOfLastOilChange, showEmptyFields), {
      label: lastOilChangeLabeledValues.dateOfLastOilChange.label ?? '',
      value: lastOilChangeLabeledValues.dateOfLastOilChange.value ?? EMPTY_PLACEHOLDER,
    })
    .when(
      isAttributeVisible(lastOilChangeLabeledValues.amountOfKmDrivenFromOilChange, showEmptyFields),
      {
        label: lastOilChangeLabeledValues.amountOfKmDrivenFromOilChange.label ?? '',
        value: lastOilChangeLabeledValues.amountOfKmDrivenFromOilChange.value ?? EMPTY_PLACEHOLDER,
      }
    );

  const lastBrakeFluidChangeAttributes = buildArray<DocumentationAttribute>()
    .when(
      isAttributeVisible(
        lastBrakeFluidChangeLabeledValues.dateOfLastBrakeFluidChange,
        showEmptyFields
      ),
      {
        label: lastBrakeFluidChangeLabeledValues.dateOfLastBrakeFluidChange.label ?? '',
        value:
          lastBrakeFluidChangeLabeledValues.dateOfLastBrakeFluidChange.value ?? EMPTY_PLACEHOLDER,
      }
    )
    .when(
      isAttributeVisible(
        lastBrakeFluidChangeLabeledValues.amountOfKmDrivenFromBrakeFluidChange,
        showEmptyFields
      ),
      {
        label: lastBrakeFluidChangeLabeledValues.amountOfKmDrivenFromBrakeFluidChange.label ?? '',
        value:
          lastBrakeFluidChangeLabeledValues.amountOfKmDrivenFromBrakeFluidChange.value ??
          EMPTY_PLACEHOLDER,
      }
    );

  const lastOilChangeInGearboxAttributes = buildArray<DocumentationAttribute>()
    .when(
      isAttributeVisible(
        lastOilChangeInGearboxLabeledValues.dateOfLastOilChangeInGearbox,
        showEmptyFields
      ),
      {
        label: lastOilChangeInGearboxLabeledValues.dateOfLastOilChangeInGearbox.label ?? '',
        value:
          lastOilChangeInGearboxLabeledValues.dateOfLastOilChangeInGearbox.value ??
          EMPTY_PLACEHOLDER,
      }
    )
    .when(
      isAttributeVisible(
        lastOilChangeInGearboxLabeledValues.amountOfKmDrivenFromOilChangeInGearbox,
        showEmptyFields
      ),
      {
        label:
          lastOilChangeInGearboxLabeledValues.amountOfKmDrivenFromOilChangeInGearbox.label ?? '',
        value:
          lastOilChangeInGearboxLabeledValues.amountOfKmDrivenFromOilChangeInGearbox.value ??
          EMPTY_PLACEHOLDER,
      }
    );

  const lastOilChangeInDifferentialAttributes = buildArray<DocumentationAttribute>()
    .when(
      isAttributeVisible(
        lastOilChangeInDifferentialLabeledValues.dateOfLastOilChangeInDifferential,
        showEmptyFields
      ),
      {
        label:
          lastOilChangeInDifferentialLabeledValues.dateOfLastOilChangeInDifferential.label ?? '',
        value:
          lastOilChangeInDifferentialLabeledValues.dateOfLastOilChangeInDifferential.value ??
          EMPTY_PLACEHOLDER,
      }
    )
    .when(
      isAttributeVisible(
        lastOilChangeInDifferentialLabeledValues.amountOfKmDrivenFromOilChangeInDifferential,
        showEmptyFields
      ),
      {
        label:
          lastOilChangeInDifferentialLabeledValues.amountOfKmDrivenFromOilChangeInDifferential
            .label ?? '',
        value:
          lastOilChangeInDifferentialLabeledValues.amountOfKmDrivenFromOilChangeInDifferential
            .value ?? EMPTY_PLACEHOLDER,
      }
    );
  const allFeatures = [
    ...lastOilChangeAttributes,
    ...lastBrakeFluidChangeAttributes,
    ...lastOilChangeInGearboxAttributes,
    ...lastOilChangeInDifferentialAttributes,
  ];
  const features = {
    serviceHistoryAttributes,
    lastOilChangeAttributes,
    lastBrakeFluidChangeAttributes,
    lastOilChangeInGearboxAttributes,
    lastOilChangeInDifferentialAttributes,
  };
  return {allFeatures, features};
};
